/**
 * Presentation helpers for `EventDoc` — the web port of the computed properties on iOS `Event`
 * (`Event.swift:170-305`).
 *
 * Kept as free functions rather than methods so they stay trivially unit-testable and so nothing
 * has to wrap the raw Firestore document in a class on the way out of `EventsService`.
 *
 * DATES ARE LOCALIZED. iOS formats with fixed `dd` / `MMM` / `EEE` patterns, so a Macedonian guest
 * reads "Apr". Here month and weekday words come from the guest's current language, matching how the
 * rest of the web menu treats guest language. The *layout* of each string (separators, comma before
 * the year, when the year appears at all) is byte-for-byte the iOS logic — only the words differ.
 *
 * The words come from a bundled table (`event-date-names.ts`) rather than `Intl.DateTimeFormat`,
 * because Intl silently returns English for languages Chrome ships no CLDR data for — `mk` and `sq`
 * among them. See that file for the full reasoning.
 */

import {
  EventDoc,
  EventLocation,
  EventMediaItem,
  EventTimestamp,
} from '../models/event.model';
import { dateNamesFor } from './event-date-names';

/** What iOS renders when an event carries no date at all. */
export const NO_DATE_PLACEHOLDER = '-';

/** Which piece of a date the badge wants. */
export type EventDatePart = 'day' | 'month' | 'weekday';

/**
 * A price ready to render. iOS builds an `AttributedString` with per-run fonts; the web returns the
 * parts and lets the template style them, so no markup is generated in TypeScript.
 */
export type EventPriceDisplay =
  | { kind: 'free' }
  | { kind: 'single'; amount: string; currency: string }
  | { kind: 'from'; amount: string; currency: string };

/** What the "Book now" button should do, or null when the event takes no booking. */
export type EventBookKind = 'call' | 'link';

// ---------------------------------------------------------------------------
// dates
// ---------------------------------------------------------------------------

/**
 * Resolve a stored timestamp to a `Date`, or null when it is absent or unusable.
 *
 * Handles all three forms the value can arrive in: a Firestore `Timestamp` (the live SDK path), a
 * `Date` (a hand-built object in a test), and an ISO string (anything that has been through
 * `JSON.stringify`, e.g. a sessionStorage round-trip). An unparseable string yields null rather
 * than an Invalid Date, so callers only ever branch on presence.
 */
export function toDate(value: EventTimestamp | null | undefined): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof (value as any).toDate === 'function') {
    try {
      const parsed = (value as any).toDate();
      return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function eventStartDate(event: EventDoc | null | undefined): Date | null {
  return toDate(event?.eventDate?.startDate);
}

/** Set on multi-day events only — 9 of 190 production documents at the time of writing. */
export function eventEndDate(event: EventDoc | null | undefined): Date | null {
  return toDate(event?.eventDate?.endDate);
}

/**
 * Month and weekday words come from the bundled table, not `Intl.DateTimeFormat` — see the note in
 * `event-date-names.ts`: Chrome ships no CLDR data for `mk` or `sq` (it resolves both to `en-GB`),
 * and CLDR's abbreviated Bulgarian month is the number "04".
 *
 * Day and year are plain numbers, formatted here rather than through `Intl`, since none of the 15
 * supported languages uses non-Western digits and this keeps the output identical everywhere.
 */
function formatPart(date: Date, lang: string, part: EventDatePart | 'year'): string {
  const names = dateNamesFor(lang);
  switch (part) {
    case 'day':
      return String(date.getDate()).padStart(2, '0');
    case 'month':
      return names.months[date.getMonth()];
    case 'weekday':
      return names.weekdays[date.getDay()];
    case 'year':
      return String(date.getFullYear());
  }
}

/**
 * One component of the event's date for the date badge — iOS `formattedEventDateComponent`.
 *
 * Returns `"-"` when there is no start date, and joins start and end with `" - "` when they differ
 * in that component (so a 22-28 April event shows "22 - 28" as its day but a single "Apr" as its
 * month).
 */
export function eventDatePart(
  event: EventDoc | null | undefined,
  part: EventDatePart,
  lang: string
): string {
  const start = eventStartDate(event);
  if (!start) return NO_DATE_PLACEHOLDER;

  const startText = formatPart(start, lang, part);

  const end = eventEndDate(event);
  if (end) {
    const endText = formatPart(end, lang, part);
    if (endText !== startText) return `${startText} - ${endText}`;
  }
  return startText;
}

/**
 * The long date line — iOS `eventDateFormattedVariantTwo`.
 *
 * Three shapes, exactly as iOS assembles them:
 *   no end date                → "22 Apr"                 (deliberately no year)
 *   end date, same month       → "22 - 28 Apr, 2025"
 *   end date, different months → "22 Apr - 03 May, 2025"
 *
 * The year comes off the END date in every case that shows one, which is iOS behaviour and matters
 * for a range that crosses New Year.
 */
export function formatEventDateRange(event: EventDoc | null | undefined, lang: string): string {
  const start = eventStartDate(event);
  if (!start) return '';

  const startDay = formatPart(start, lang, 'day');
  const startMonth = formatPart(start, lang, 'month');

  const end = eventEndDate(event);
  if (!end) return `${startDay} ${startMonth}`;

  const endDay = formatPart(end, lang, 'day');
  const endMonth = formatPart(end, lang, 'month');
  const year = formatPart(end, lang, 'year');

  return endMonth === startMonth
    ? `${startDay} - ${endDay} ${endMonth}, ${year}`
    : `${startDay} ${startMonth} - ${endDay} ${endMonth}, ${year}`;
}

/**
 * The time line — iOS `eventStartTimeFormattedVariantTwo`. "11:00 - 14:30", or just "11:00" when
 * there is no end time (the common case: most events carry a start only).
 *
 * The stored values are free text in "HH:mm" 24h, not timestamps, so they are passed through
 * untouched — there is nothing to localize and nothing to parse.
 */
export function formatEventTimeRange(event: EventDoc | null | undefined): string {
  const start = (event?.eventTime?.startTime ?? '').trim();
  const end = (event?.eventTime?.endTime ?? '').trim();

  if (!start) return '';
  return end ? `${start} - ${end}` : start;
}

// ---------------------------------------------------------------------------
// media
// ---------------------------------------------------------------------------

/**
 * The still for one media item — a video's poster, or the image itself. Null when it has none.
 *
 * A video's `.mp4` URL is never returned: in an `<img>` it renders a broken-image icon, and a
 * poster-less video shows its own first frame through the `<video>` element instead.
 */
export function mediaStillUrl(item: EventMediaItem | null | undefined): string | null {
  const thumbnail = (item?.mediaThumbnailUrl ?? '').trim();
  if (isVideoMedia(item)) return thumbnail || null;
  return (item?.mediaUrl ?? '').trim() || thumbnail || null;
}

/**
 * True when an item can be shown at all — a playable video, or an image with a URL.
 *
 * Exported because the promotion media gallery filters with the same rule; keeping one definition
 * is what stops "renderable" from meaning two slightly different things in two features.
 */
export function isRenderableMedia(item: EventMediaItem | null | undefined): boolean {
  if (!item) return false;
  if (isVideoMedia(item)) return !!(item.mediaUrl ?? '').trim();
  return !!mediaStillUrl(item);
}

/**
 * The media a card or the details hero leads with, or null when the event ships nothing renderable.
 *
 * Walks `mediaItems` in Firestore order and takes the first item that can be shown — order is the
 * admin's choice, so this does not prefer images over videos or vice versa. A poster-less video
 * still counts: `app-event-media` plays it and its first frame stands in for the missing poster.
 *
 * Null is a real case every caller must handle — 13 dev documents ship no media at all.
 */
export function heroMedia(event: EventDoc | null | undefined): EventMediaItem | null {
  for (const item of event?.mediaItems ?? []) {
    if (isRenderableMedia(item)) return item;
  }
  return null;
}

/**
 * The still for the hero slot, without the video. Used where only a flat image will do — never for
 * the card hero itself, which goes through `app-event-media` so video actually plays.
 */
export function heroImageUrl(event: EventDoc | null | undefined): string | null {
  return mediaStillUrl(heroMedia(event));
}

/**
 * Case-insensitive `mediaType === 'video'`; anything else, absent, or null is an image.
 *
 * One production document stores the key with a leading space (`" mediaType"`), which decodes as a
 * missing `mediaType` — so "absent means image" is load-bearing, not just a default.
 */
export function isVideoMedia(item: EventMediaItem | null | undefined): boolean {
  return (item?.mediaType ?? '').trim().toLowerCase() === 'video';
}

// ---------------------------------------------------------------------------
// location
// ---------------------------------------------------------------------------

export function primaryLocation(event: EventDoc | null | undefined): EventLocation | null {
  return event?.locations?.[0] ?? null;
}

/**
 * The short place line under a title.
 *
 * iOS reads `city` alone, and `city` is populated on ZERO locations across both projects — so that
 * line is blank on every iOS card today. Falling back `city → place → country` fills it from data
 * that is actually there, which is why this diverges deliberately.
 */
export function locationLabel(location: EventLocation | null | undefined): string {
  const city = (location?.city ?? '').trim();
  if (city) return city;

  const place = (location?.place ?? '').trim();
  if (place) return place;

  return (location?.country ?? '').trim();
}

// ---------------------------------------------------------------------------
// identity
// ---------------------------------------------------------------------------

/** Who an event is presented by, resolved once for the details screen. */
export interface EventIdentity {
  name: string;
  logoUrl: string | null;
  /**
   * True when the name is the ORGANISER of a city event rather than a venue. The view must caption
   * it — a name sitting in the venue's slot with no label reads as the venue the diner is in.
   */
  isOrganiser: boolean;
}

/**
 * Business → organiser → nothing (tracker **I-21**, GZ's ruling, already shipped on iOS and Android).
 *
 * A city event has `businessName` and `businessLogoUrl` null by design (**D-6**), so the details
 * screen used to draw an empty avatar over a blank bold line. `organizerName` is what replaces it —
 * and returning null rather than a blank identity is what lets the view drop the row entirely
 * instead of rendering an empty one.
 *
 * ⚠️ The caption is not cosmetic: **we are not the organiser** (**R-4**), and an uncaptioned name in
 * the venue's position claims a relationship that does not exist.
 */
export function eventIdentity(event: EventDoc | null | undefined): EventIdentity | null {
  const businessName = (event?.businessName ?? '').trim();
  if (businessName) {
    return {
      name: businessName,
      logoUrl: (event?.businessLogoUrl ?? '').trim() || null,
      isOrganiser: false,
    };
  }

  const organizerName = (event?.organizerName ?? '').trim();
  if (organizerName) {
    return {
      name: organizerName,
      logoUrl: (event?.organizerLogoUrl ?? '').trim() || null,
      isOrganiser: true,
    };
  }

  return null;
}

/** True only when both coordinates are real numbers — 0,0 is a valid point, so don't test falsiness. */
export function hasCoordinates(location: EventLocation | null | undefined): boolean {
  return typeof location?.latitude === 'number' && typeof location?.longitude === 'number';
}

// ---------------------------------------------------------------------------
// price
// ---------------------------------------------------------------------------

/** iOS drops `.00` and otherwise shows exactly two decimals. */
function formatAmount(price: number): string {
  return price % 1 === 0 ? String(price) : price.toFixed(2);
}

/**
 * The price pill — iOS `priceFormat`.
 *
 * Note the first branch: an event with NO prices at all reads as free, because iOS's
 * `allSatisfy` is vacuously true on an empty array. 21 of 50 sampled documents in each project have
 * an empty `prices` array, so this is the single most common outcome, not an edge case. Preserved
 * deliberately for cross-platform parity — changing it is a product decision, not a porting one.
 */
export function eventPrice(event: EventDoc | null | undefined): EventPriceDisplay {
  const amounts = (event?.prices ?? [])
    .map(p => p?.price)
    .filter((p): p is number => typeof p === 'number');

  if (amounts.every(p => p === 0)) return { kind: 'free' };

  // Blank / absent on ~14 documents per project; the pill then shows a bare number.
  const currency = (event?.currency ?? '').trim();
  const nonZero = amounts.filter(p => p > 0);

  if (nonZero.length === 1) {
    return { kind: 'single', amount: formatAmount(nonZero[0]), currency };
  }
  return { kind: 'from', amount: formatAmount(Math.min(...nonZero)), currency };
}

// ---------------------------------------------------------------------------
// actions
// ---------------------------------------------------------------------------

/**
 * Whether the promotion badge shows. Backed by `relatedVoucherIDs`, which is empty on every
 * document in both projects — so this returns false in production today and the badge self-hides.
 */
export function hasPromotion(event: EventDoc | null | undefined): boolean {
  return (event?.relatedVoucherIDs ?? []).length > 0;
}

/**
 * What "Book now" does, or null when the event takes no booking (37 of 190 production documents).
 * A recognised type with a blank value is also null — a button that opens `tel:` with no number is
 * worse than no button.
 */
export function bookActionKind(event: EventDoc | null | undefined): EventBookKind | null {
  const type = (event?.bookAction?.type ?? '').trim();
  const value = (event?.bookAction?.value ?? '').trim();
  if (!value) return null;

  if (type === 'call') return 'call';
  if (type === 'navigationLink') return 'link';
  return null;
}

/**
 * A maps URL for the event's location, or null without coordinates.
 *
 * iOS offers an Apple/Google confirmation dialog. On the web a single universal Google Maps link is
 * the equivalent — it opens the native app on both platforms when one is installed — except on iOS
 * Safari, where `maps.apple.com` hands off to Apple Maps directly rather than bouncing through a
 * web page. Detection is by platform, not by browser: an iPhone Chrome user still wants Apple Maps.
 */
export function directionsUrl(location: EventLocation | null | undefined): string | null {
  if (!hasCoordinates(location)) return null;
  const coords = `${location!.latitude},${location!.longitude}`;

  return isApplePlatform()
    ? `https://maps.apple.com/?q=${coords}`
    : `https://www.google.com/maps/search/?api=1&query=${coords}`;
}

function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  // iPadOS 13+ reports as "MacIntel" with a touch screen, so the platform string alone is not
  // enough to tell an iPad from a Mac — and a Mac wants Apple Maps too, so both land here anyway.
  return /iPad|iPhone|iPod|Mac/.test(navigator.platform || '');
}
